import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Search, Filter, Globe, Eye, RefreshCw, FileText, User, Mail, Phone, MapPin, Calendar, Users, MessageSquare, Clock } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import PageLoader from "../components/PageLoader";
import EmptyState from "../components/EmptyState";
import StatusBadge from "../components/StatusBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRY_FLAGS = {
  "United Kingdom": "🇬🇧", "United States": "🇺🇸", "Canada": "🇨🇦",
  "United Arab Emirates": "🇦🇪", "Qatar": "🇶🇦", "Saudi Arabia": "🇸🇦",
  "Pakistan": "🇵🇰", "Malaysia": "🇲🇾", "Singapore": "🇸🇬",
  "Vietnam": "🇻🇳", "Azerbaijan": "🇦🇿", "Bahrain": "🇧🇭",
  "Oman": "🇴🇲", "Kuwait": "🇰🇼", "Turkey": "🇹🇷",
  "Thailand": "🇹🇭", "Indonesia": "🇮🇩", "China": "🇨🇳",
  "India": "🇮🇳", "Schengen (EU)": "🇪🇺",
};


function DetailField({ icon: Icon, label, children }) {
  if (!children) return null;
  return (
    <div className="flex gap-3 py-2.5 border-b border-gray-50 last:border-0">
      {Icon && <Icon className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />}
      <div className="min-w-0">
        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">{label}</p>
        <div className="text-sm text-gray-800">{children}</div>
      </div>
    </div>
  );
}

export default function VisaRequests() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [requests,       setRequests]       = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [searchTerm,     setSearchTerm]     = useState("");
  const [statusFilter,   setStatusFilter]   = useState("all");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [quoteInfo,      setQuoteInfo]      = useState(null);
  const [loadingQuote,   setLoadingQuote]   = useState(false);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== "all" ? { status: statusFilter } : {};
      const response = await axios.get(`${API}/visa-requests`, { headers: getAuthHeaders(), params });
      setRequests(response.data);
    } catch (error) {
      console.error("Failed to fetch visa requests:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRequests(); }, [statusFilter]);

  useEffect(() => {
    if (!selectedRequest) { setQuoteInfo(null); return; }
    const fetchQuote = async () => {
      setLoadingQuote(true);
      try {
        const res = await axios.get(`${API}/visa-quotes`, {
          headers: getAuthHeaders(),
          params: { visa_request_id: selectedRequest.id },
        });
        setQuoteInfo(res.data.length > 0 ? res.data[0] : null);
      } catch { setQuoteInfo(null); }
      finally { setLoadingQuote(false); }
    };
    fetchQuote();
  }, [selectedRequest?.id]);

  const filteredRequests = requests.filter((r) => {
    const s = searchTerm.toLowerCase();
    return (
      r.first_name?.toLowerCase().includes(s) ||
      r.last_name?.toLowerCase().includes(s) ||
      r.email?.toLowerCase().includes(s) ||
      r.destination_country?.toLowerCase().includes(s) ||
      r.request_number?.toLowerCase().includes(s)
    );
  });

  const handleStatusUpdate = async (id, newStatus) => {
    setUpdatingStatus(true);
    try {
      await axios.patch(`${API}/visa-requests/${id}`, { status: newStatus }, { headers: getAuthHeaders() });
      setRequests((prev) => prev.map((r) => r.id === id ? { ...r, status: newStatus } : r));
      if (selectedRequest?.id === id) setSelectedRequest((prev) => ({ ...prev, status: newStatus }));
      toast.success("Status updated");
    } catch { toast.error("Failed to update status"); }
    finally { setUpdatingStatus(false); }
  };

  const req = selectedRequest;

  return (
    <DashboardLayout>
      <div className="p-6 sm:p-8">
        {/* Header */}
        <PageHeader
          title="Visa Requests"
          subtitle="Manage and process customer visa applications"
          actions={
            <Button onClick={fetchRequests} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />Refresh
            </Button>
          }
        />

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by name, email, country..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <PageLoader inline message="Loading visa requests…" />
            ) : filteredRequests.length === 0 ? (
              <EmptyState
                title="No visa requests"
                description={searchTerm || statusFilter !== "all" ? "Try adjusting your filters." : "Visa requests will appear here once submitted."}
                icon={Globe}
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Request #</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Country</TableHead>
                    <TableHead>Visa Type</TableHead>
                    <TableHead>Travellers</TableHead>
                    <TableHead>Travel Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRequests.map((r) => (
                    <TableRow
                      key={r.id}
                      className="table-row-hover cursor-pointer"
                      onClick={() => setSelectedRequest(r)}
                    >
                      <TableCell>
                        <span className="font-mono font-bold text-indigo-700">{r.request_number || "—"}</span>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-gray-900">{r.first_name} {r.last_name}</p>
                          <p className="text-sm text-gray-500">{r.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1.5">
                          <span>{COUNTRY_FLAGS[r.destination_country] || "🌍"}</span>
                          {r.destination_country}
                        </span>
                      </TableCell>
                      <TableCell>{r.visa_type}</TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {r.applicants_adults}A{r.applicants_children > 0 && ` ${r.applicants_children}C`}
                        </span>
                      </TableCell>
                      <TableCell><span className="text-sm text-gray-500">{r.travel_date || "—"}</span></TableCell>
                      <TableCell>
                        <StatusBadge status={r.status} />
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-gray-500">
                          {r.created_at ? format(new Date(r.created_at), "MMM d, yyyy") : "—"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setSelectedRequest(r); }}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Detail modal */}
        <Dialog open={!!selectedRequest} onOpenChange={(open) => { if (!open) setSelectedRequest(null); }}>
          <DialogContent className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {req && (
              <>
                <DialogHeader className="pb-2 border-b border-gray-100">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-mono font-bold text-indigo-700 text-lg">{req.request_number}</span>
                        <StatusBadge status={req.status} />
                      </div>
                      <DialogTitle className="text-base font-semibold text-gray-800">
                        {req.first_name} {req.last_name} — {COUNTRY_FLAGS[req.destination_country] || "🌍"} {req.destination_country}
                      </DialogTitle>
                    </div>
                  </div>
                </DialogHeader>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 mt-2">
                  {/* Left column */}
                  <div>
                    <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1 mt-1">Customer</p>
                    <DetailField icon={User}     label="Name">{req.first_name} {req.last_name}</DetailField>
                    <DetailField icon={Mail}     label="Email">{req.email}</DetailField>
                    <DetailField icon={Phone}    label="Phone">{req.phone}</DetailField>

                    <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1 mt-4">Application</p>
                    <DetailField icon={MapPin}   label="Destination">
                      <span className="inline-flex items-center gap-1.5">
                        <span>{COUNTRY_FLAGS[req.destination_country] || "🌍"}</span>
                        {req.destination_country}
                      </span>
                    </DetailField>
                    <DetailField icon={FileText} label="Visa Type">{req.visa_type}</DetailField>
                    <DetailField icon={Globe}    label="Nationality">{req.nationality}</DetailField>
                    <DetailField icon={Calendar} label="Travel Date">{req.travel_date}</DetailField>
                    <DetailField icon={Users}    label="Travellers">
                      {req.applicants_adults} Adult{req.applicants_adults !== 1 ? "s" : ""}
                      {req.applicants_children > 0 && `, ${req.applicants_children} Child${req.applicants_children !== 1 ? "ren" : ""}`}
                    </DetailField>
                    <DetailField icon={Clock}    label="Submitted">
                      {req.created_at ? format(new Date(req.created_at), "d MMMM yyyy") : "—"}
                    </DetailField>
                  </div>

                  {/* Right column */}
                  <div>
                    {req.customer_notes && (
                      <>
                        <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1 mt-1">Customer Notes</p>
                        <div className="flex gap-3 py-2">
                          <MessageSquare className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                          <p className="text-sm text-gray-700 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2 leading-relaxed w-full">
                            {req.customer_notes}
                          </p>
                        </div>
                      </>
                    )}

                    {/* Status update */}
                    <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2 mt-4">Update Status</p>
                    <Select
                      value={req.status}
                      onValueChange={(v) => handleStatusUpdate(req.id, v)}
                      disabled={updatingStatus}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="processing">Processing</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>

                    {/* Quotation */}
                    <div className="mt-5 pt-4 border-t border-gray-100">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">Quotation</p>
                        {loadingQuote ? (
                          <div className="h-4 w-16 bg-gray-100 rounded animate-pulse" />
                        ) : quoteInfo ? (
                          <StatusBadge status={quoteInfo.status} />
                        ) : (
                          <Badge className="bg-gray-100 text-gray-500">Not Created</Badge>
                        )}
                      </div>
                      {quoteInfo && (
                        <p className="text-xs text-gray-500 font-mono mb-3">{quoteInfo.quote_number}</p>
                      )}
                      <Button
                        className="w-full"
                        size="sm"
                        onClick={() => navigate(`/visa-requests/${req.id}/quote`)}
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        {quoteInfo ? "View / Edit Quote" : "Generate Quote"}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end pt-4 border-t border-gray-100 mt-2">
                  <Button variant="outline" onClick={() => setSelectedRequest(null)}>Close</Button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

